import { EMAIL_REGEXP } from 'core/common/constants/emails';
import { LONG_STRING_REGEXP, UUID_AND_NUMERIC_REGEXP } from 'core/common/constants/maskPatterns';
import { normalizeError } from 'core/common/errors';
import type { ObservabilitySpan } from 'core/common/observability/entities';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { TraceContextHttpPropagator } from 'core/common/observability/services/TraceContextHttpPropagator';
import { HttpClientDecorator } from './HttpClientDecorator';
import { HttpRequestConfig } from './entities';
import { HttpClient } from './interfaces';

export class HttpClientWithObservability extends HttpClientDecorator {
  private readonly tracePropagator = new TraceContextHttpPropagator();

  constructor(
    httpClient: HttpClient,
    private readonly observabilitySystem: ObservabilitySystem,
  ) {
    super(httpClient);
  }

  async get<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config?: HttpRequestConfig,
  ) {
    return this.observabilitySystem.startActiveSpan(
      this.getSpanName('GET', url),
      async (activeSpan) => {
        try {
          const merged = this.mergeConfigWithTrace(config, activeSpan);
          const result = await super.get<ResponseData, ResponseMeta>(url, merged);

          activeSpan.succeed();

          return result;
        } catch (err: unknown) {
          const error = normalizeError(err);
          activeSpan.fail(error.message);

          throw err;
        }
      },
    );
  }

  async post<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config?: HttpRequestConfig,
  ) {
    return this.observabilitySystem.startActiveSpan(
      this.getSpanName('POST', url),
      async (activeSpan) => {
        try {
          const merged = this.mergeConfigWithTrace(config, activeSpan);
          const result = await super.post<ResponseData, ResponseMeta>(url, merged);

          activeSpan.succeed();

          return result;
        } catch (err: unknown) {
          const error = normalizeError(err);
          activeSpan.fail(error.message);

          throw err;
        }
      },
    );
  }

  async put<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config?: HttpRequestConfig,
  ) {
    return this.observabilitySystem.startActiveSpan(
      this.getSpanName('PUT', url),
      async (activeSpan) => {
        try {
          const merged = this.mergeConfigWithTrace(config, activeSpan);

          const result = await super.put<ResponseData, ResponseMeta>(url, merged);

          activeSpan.succeed();
          return result;
        } catch (err: unknown) {
          const error = normalizeError(err);
          activeSpan.fail(error.message);

          throw err;
        }
      },
    );
  }

  async patch<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config?: HttpRequestConfig,
  ) {
    return this.observabilitySystem.startActiveSpan(
      this.getSpanName('PATCH', url),
      async (activeSpan) => {
        try {
          const merged = this.mergeConfigWithTrace(config, activeSpan);
          const result = await super.patch<ResponseData, ResponseMeta>(url, merged);

          activeSpan.succeed();

          return result;
        } catch (err: unknown) {
          const error = normalizeError(err);
          activeSpan.fail(error.message);

          throw err;
        }
      },
    );
  }

  async delete<ResponseData = unknown, ResponseMeta = unknown>(
    url: string,
    config?: HttpRequestConfig,
  ) {
    return this.observabilitySystem.startActiveSpan(
      this.getSpanName('DELETE', url),
      async (activeSpan) => {
        try {
          const merged = this.mergeConfigWithTrace(config, activeSpan);
          const result = await super.delete<ResponseData, ResponseMeta>(url, merged);

          activeSpan.succeed();

          return result;
        } catch (err: unknown) {
          const error = normalizeError(err);
          activeSpan.fail(error.message);

          throw err;
        }
      },
    );
  }

  private getSpanName(method: string, url: string) {
    const pathname = url
      .replace(UUID_AND_NUMERIC_REGEXP, '_uuid_or_int_')
      .replace(EMAIL_REGEXP, '_email_')
      .replace(LONG_STRING_REGEXP, '_long_string_');

    return `${method} ${pathname}`;
  }

  private mergeConfigWithTrace(
    config: HttpRequestConfig = {},
    span?: ObservabilitySpan,
  ): HttpRequestConfig {
    return {
      ...config,
      headers: {
        ...config.headers,
        ...this.tracePropagator.injectHeaders(span),
      },
    };
  }
}
